import { FC } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { useFragment, graphql } from 'react-relay';
import { getProductionTimeNotePart } from '../helpers/productionTimeNoteHelper';
import { getCheckoutLeadTimeCopy } from '../helpers/getCheckoutLeadTimeCopy';
import { useIntl } from 'dibs-react-intl';

// components
import SharedFormatAmount from '../components/SharedFormatAmount';

import { PreQuoteTitle_shipmentQuote$key } from './__generated__/PreQuoteTitle_shipmentQuote.graphql';
import { PreQuoteTitle_sku$key } from './__generated__/PreQuoteTitle_sku.graphql';
import { PreQuoteTitle_transaction$key } from './__generated__/PreQuoteTitle_transaction.graphql';

type Props = {
    offerCurrency: string;
    sku: PreQuoteTitle_sku$key | null;
    shipmentQuote: PreQuoteTitle_shipmentQuote$key | null;
    transaction: PreQuoteTitle_transaction$key | null;
};

export const PreQuoteTitle: FC<Props> = ({
    shipmentQuote: shipmentQuoteRef,
    offerCurrency,
    sku: skuRef,
    transaction: transactionRef,
}) => {
    const intl = useIntl();
    const shipmentQuote = useFragment(
        graphql`
            fragment PreQuoteTitle_shipmentQuote on ShipmentQuote {
                isFreeToBuyer
                buyerServiceMethodDisplay
                handlingTimeDisplay
                buyerTotalAmount {
                    ...SharedFormatAmount_financialAmount
                }
            }
        `,
        shipmentQuoteRef
    );
    const sku = useFragment(
        graphql`
            fragment PreQuoteTitle_sku on ItemSkuType {
                isAvailableNow
            }
        `,
        skuRef
    );
    const transaction = useFragment(
        graphql`
            fragment PreQuoteTitle_transaction on Transaction {
                ...productionTimeNoteHelper_transaction @relay(mask: false)
                sku {
                    leadTime(pageType: Checkout)
                }
                leadTime {
                    value
                }
            }
        `,
        transactionRef
    );
    const { buyerServiceMethodDisplay, handlingTimeDisplay, buyerTotalAmount, isFreeToBuyer } =
        shipmentQuote || {};
    const isAvailableNow = sku?.isAvailableNow;
    const leadTime = getCheckoutLeadTimeCopy(
        transaction?.leadTime?.value || transaction?.sku?.leadTime || ''
    );

    const note = getProductionTimeNotePart({ transaction, hideOnOffer: false });
    return (
        <span>
            {!!buyerTotalAmount && (
                <span data-tn="delivery-options-amount">
                    <SharedFormatAmount
                        currency={offerCurrency}
                        financialAmount={buyerTotalAmount || null}
                    />
                </span>
            )}{' '}
            {isFreeToBuyer && (
                <FormattedMessage
                    id="checkout.CheckoutComplimentary.message"
                    defaultMessage="Free shipping,"
                />
            )}{' '}
            <span data-tn="delivery-options-method">{buyerServiceMethodDisplay}</span>
            <span data-tn="delivery-options-time-frame">
                <FormattedMessage
                    id="checkout.CheckoutComplimentary.arrivesIn"
                    defaultMessage=", arrives in {handlingTimeDisplay}"
                    values={{ handlingTimeDisplay }}
                />
            </span>
            {!isAvailableNow && note ? (
                <FormattedMessage
                    id="checkout.CheckoutComplimentary.leadTime"
                    defaultMessage=", excluding production time of {leadTime}, {note}"
                    values={{ leadTime, note: intl.formatMessage(note) }}
                />
            ) : null}
        </span>
    );
};

export default PreQuoteTitle;
